import { useState } from "react";
import { Button, Heading, Text, VStack } from "@astryxdesign/core";
import * as stylex from "@stylexjs/stylex";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * "Join {carrier} as a driver?" — the in-app half of the invite flow.
 *
 * A driver who signs in before accepting has no operator access yet, so they
 * would otherwise land on the no-access dead end. This turns that screen into
 * the accept step, which is what removes the browser round-trip.
 *
 * Deliberately an explicit tap. The server reveals the invite on sign-in but
 * only grants access on the accept call — if logging in were enough, an invite
 * sent to a shared mailbox would be claimed silently by whoever signed in
 * first. The prompt names the carrier so the driver sees what they are joining.
 */

export interface PendingInvite {
  id: string;
  role: string;
  expiresAt: string;
  carrier: { id: string; name: string };
  dispatchCenter: { id: string; name: string } | null;
}

const styles = stylex.create({
  actionWrap: { width: "100%", maxWidth: "20rem" },
  error: { color: "var(--color-error)" },
});

export function usePendingInvite() {
  return useQuery({
    queryKey: ["pending-invite"],
    queryFn: async () => {
      const res = await api.get<{ data: PendingInvite | null }>(
        "/api/v2/carrier/invites/mine"
      );
      return res.data.data;
    },
    // A driver with no invite is the normal case, not an error worth retrying.
    retry: false,
  });
}

export function PendingInvitePrompt({
  invite,
  onAccepted,
}: {
  invite: PendingInvite;
  onAccepted: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: async () => {
      const res = await api.post("/api/v2/carrier/invites/mine/accept");
      return res.data;
    },
    onSuccess: async () => {
      // The invite is consumed and the membership now exists; everything that
      // depended on "no access" has to be re-read.
      await queryClient.invalidateQueries();
      onAccepted();
    },
    onError: (caught: unknown) => {
      const status = (caught as { response?: { status?: number } })?.response
        ?.status;
      setError(
        status === 404
          ? "This invitation is no longer available. Ask dispatch to resend it."
          : "Could not join right now. Please try again."
      );
    },
  });

  const where = invite.dispatchCenter?.name;

  return (
    <>
      <VStack align="center" gap={1}>
        <Heading level={1}>Join {invite.carrier.name}?</Heading>
        <Text size="sm" color="secondary">
          {where
            ? `You've been invited to drive for ${invite.carrier.name} out of ${where}.`
            : `You've been invited to drive for ${invite.carrier.name}.`}
        </Text>
      </VStack>
      <div {...stylex.props(styles.actionWrap)}>
        <VStack gap={2}>
          <Button
            variant="primary"
            size="lg"
            width="100%"
            label={accept.isPending ? "Joining…" : "Accept invitation"}
            isDisabled={accept.isPending}
            onClick={() => {
              setError(null);
              accept.mutate();
            }}
          />
          {error && (
            <Text size="sm" {...stylex.props(styles.error)}>
              <span role="alert">{error}</span>
            </Text>
          )}
        </VStack>
      </div>
    </>
  );
}
